/**
 * The one way the studio talks to the control plane.
 *
 * There used to be five of these. `api.ts` had the real one; `CommentThread`,
 * `RunTimeline`, `FleetView`, `KnowledgeView` and `ArtifactsView` each had a
 * shorter copy, written when a screen needed an endpoint the shared client did
 * not expose. The copies were not equivalent, and the differences were the
 * kind nobody notices until it matters:
 *
 * - **None of them handled 401.** A session that expired while a screen was
 *   open showed a raw error instead of signing the reader back in.
 * - **Each unwrapped errors differently.** One read `message`, one read
 *   `message ?? error`, one read `message ?? reason`. A refusal the server
 *   explained in the field a given copy did not read surfaced as
 *   "403 Forbidden" — which is exactly the wording the shared error handler on
 *   the Worker exists to replace.
 * - **None knew the typed refusals.** A 428 carrying the connections a run
 *   needs, or a 409 from a lost editor race, arrived as a bare `Error`.
 *
 * One transport, so a screen added tomorrow gets all of that by default rather
 * than by remembering.
 */
import type { MissingConnection } from '@reef/api-contract';

export class ConnectionsRequiredError extends Error {
  constructor(readonly missing: MissingConnection[]) {
    super('Some connections need to be authorized before this can run.');
    this.name = 'ConnectionsRequiredError';
  }
}

/**
 * The deployment's own ceiling, not the workflow's.
 *
 * Kept separate from a generic error because the remedy is different and only
 * an admin has it: waiting will clear a concurrency ceiling, and nothing the
 * caller does will clear a spend one.
 */
export class DeploymentAtCapacityError extends Error {
  constructor(
    message: string,
    readonly ceiling: string,
  ) {
    super(message);
    this.name = 'DeploymentAtCapacityError';
  }
}

export class StaleEditorError extends Error {
  /**
   * Where to reload from, in whichever form the surface has one.
   *
   * `revision` for a workflow or a factory, which are D1 documents; `path` for
   * a policy, which is still a file in the config repo. Both optional and both
   * carried, because the same 409 comes from two write paths and a view that
   * assumed one shape rendered "undefined" for the other.
   */
  constructor(
    readonly path: string,
    readonly revision: number | null = null,
  ) {
    super('Someone else saved this while you were editing. Reload to pick up their version.');
    this.name = 'StaleEditorError';
  }
}

/**
 * A create aimed at an id that is taken.
 *
 * Its own class rather than a `StaleEditorError`, because the action it asks
 * for is different: reloading changes nothing, and the caller has to choose
 * another id or open the one that exists.
 */
export class AlreadyExistsError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AlreadyExistsError';
  }
}

/**
 * Start the Okta sign-in — at most once per page.
 *
 * better-auth's generic-OAuth sign-in is a `POST` that *returns* the provider
 * URL — navigating to it directly is a 404, and the redirect it would have
 * issued is also what sets the state cookie the callback later validates. So
 * the browser has to make the call and then follow the URL it hands back.
 *
 * **Which is why this deduplicates.** Each call mints a fresh OAuth state and
 * sets `better-auth.state` to it, so a second call overwrites the first's
 * cookie — and then the browser follows whichever `location` was assigned last,
 * which need not be the one the surviving cookie names. better-auth correctly
 * refuses that as `state_mismatch`, and the symptom is an error page nobody can
 * reproduce by hand.
 *
 * Two callers race here routinely, and neither is a mistake: React's
 * `StrictMode` invokes the session effect twice in development, and a cold page
 * load fires four control-plane requests at once that all come back 401. So the
 * promise is memoized at module scope rather than guarded per component —
 * signing in is a one-way door, and starting it twice is never right.
 */
let signInStarted: Promise<never> | null = null;

/*
 * The same guard again, across tabs.
 *
 * The memo above is module scope, which is one tab. `better-auth.state` is a
 * *cookie*, which is the whole origin — so two tabs deduplicate perfectly and
 * still trample each other, and the second one's state is the one that survives
 * while the first one's redirect is the one being followed. That is
 * `state_mismatch`, and it needs no mistake to reach: a studio left open in a
 * background tab gets 401s of its own and starts its own sign-in, so the tab
 * somebody is actually signing in with loses to a tab they had forgotten about.
 *
 * `localStorage` is shared across tabs of an origin and its writes are atomic,
 * so a claim in it is enough to pick one winner. The claim carries a timestamp
 * rather than a lock that must be released: a tab that wins and is then closed
 * mid-flow would otherwise lock the origin out until the storage was cleared by
 * hand, which is a worse failure than the one being fixed.
 */
const CLAIM_KEY = 'reef.signin.claim';
const DONE_KEY = 'reef.signin.done';

/*
 * Long enough for Okta, short enough to recover from an abandoned attempt.
 *
 * The window has to cover a real sign-in — an SSO redirect, possibly an MFA
 * prompt — because a claim that expires while somebody is typing a code lets a
 * second tab mint a state and reintroduces exactly this bug. Two minutes is
 * past the point where a person who has walked away is coming back, and any tab
 * still waiting simply reloads and tries again.
 */
const CLAIM_TTL_MS = 120_000;

/** The shared storage, or null where it is unavailable — private mode, or blocked. */
function sharedStore(): Storage | null {
  try {
    const store = globalThis.localStorage;
    // Touched rather than trusted: Safari in private mode exposes the object
    // and throws on write, so presence alone does not mean usable.
    const probe = '__reef_probe__';
    store.setItem(probe, '1');
    store.removeItem(probe);
    return store;
  } catch {
    return null;
  }
}

/**
 * Take the origin-wide claim, or report that another tab holds it.
 *
 * Degrades to `true` when storage is unavailable, which is deliberate: without
 * it this tab behaves exactly as it did before the guard existed. A blocked
 * `localStorage` must not be a reason nobody can sign in.
 */
function claimSignIn(now = Date.now()): boolean {
  const store = sharedStore();
  if (!store) return true;

  const held = Number(store.getItem(CLAIM_KEY) ?? 0);
  if (held && now - held < CLAIM_TTL_MS) return false;

  store.setItem(CLAIM_KEY, String(now));
  ours = String(now);
  return true;
}

/** The claim this tab wrote, so it only ever releases its own. */
let ours: string | null = null;

/**
 * Give the claim back after a start that failed.
 *
 * The memo above is released on failure so a misconfigured provider is
 * retryable rather than needing a reload — and the claim has to be released
 * with it, or the retry is refused by this tab's own claim and the deployment
 * looks unfixable for two minutes. Only its own claim: a tab that has been
 * overtaken must not clear the claim of the tab that overtook it.
 */
function releaseSignInClaim(): void {
  const store = sharedStore();
  if (!store || ours === null) return;
  if (store.getItem(CLAIM_KEY) === ours) store.removeItem(CLAIM_KEY);
  ours = null;
}

/**
 * Say the session is live, for the tabs that stood aside.
 *
 * Called once the app has a session. The losing tabs are still sitting on a
 * failed request with no way to know the cookie under them changed, and telling
 * them is cheaper than making them poll: `storage` fires only in *other* tabs,
 * which is exactly the audience.
 */
export function announceSignedIn(): void {
  const store = sharedStore();
  if (!store) return;
  store.removeItem(CLAIM_KEY);
  store.setItem(DONE_KEY, String(Date.now()));
}

/**
 * Reload when another tab finishes signing in.
 *
 * Registered once, by the app shell. Without it a tab that stood aside shows
 * "redirecting to sign in" until somebody reloads it by hand — correct, but it
 * reads as the sign-in having failed.
 */
export function reloadWhenSignedIn(): () => void {
  const onStorage = (event: StorageEvent) => {
    if (event.key === DONE_KEY && event.newValue) globalThis.location.reload();
  };
  globalThis.addEventListener('storage', onStorage);
  return () => globalThis.removeEventListener('storage', onStorage);
}

export function startOktaSignIn(callbackURL = '/'): Promise<never> {
  // Released on failure, so a misconfigured provider is a retryable error
  // rather than a page that has to be reloaded before it will try again.
  signInStarted ??= (
    claimSignIn()
      ? beginOktaSignIn(callbackURL)
      : // Another tab is mid-flow. Waiting rather than navigating is the whole
        // point: this tab must not mint a second state over the one that tab's
        // redirect is carrying. It reloads when that tab announces a session.
        new Promise<never>(() => {})
  ).catch((error) => {
    signInStarted = null;
    releaseSignInClaim();
    throw error;
  }) as Promise<never>;
  return signInStarted;
}

async function beginOktaSignIn(callbackURL: string): Promise<never> {
  const response = await fetch('/api/auth/sign-in/oauth2', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      providerId: 'okta',
      // Absolute, because the Worker and the builder are different origins — a
      // bare path would resolve against the Worker, which serves no UI.
      callbackURL: new URL(callbackURL, window.location.origin).href,
    }),
  });

  if (!response.ok) {
    throw new Error(
      `Could not start the Okta sign-in (${response.status}). Check OKTA_ISSUER, OKTA_CLIENT_ID and OKTA_CLIENT_SECRET.`,
    );
  }

  const { url } = (await response.json()) as { url?: string };
  if (!url) throw new Error('Okta sign-in did not return a redirect URL.');

  window.location.href = url;
  // Navigation is asynchronous; block the caller from continuing meanwhile.
  return new Promise<never>(() => {});
}

export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`/api/v1${path}`, {
    credentials: 'include',
    ...init,
    headers: { 'content-type': 'application/json', ...init.headers },
  });

  if (response.ok) return (await response.json()) as T;

  const body = (await response.json().catch(() => ({}))) as Record<string, unknown>;

  if (response.status === 401) {
    // Deduplicated inside, so the four requests a cold page load fires start
    // one sign-in between them. The catch is here because this caller throws
    // its own error and never awaits the redirect.
    void startOktaSignIn(window.location.pathname).catch(() => {});
    throw new Error('redirecting to sign in');
  }
  if (response.status === 428 && Array.isArray(body.missing)) {
    throw new ConnectionsRequiredError(body.missing as MissingConnection[]);
  }
  /*
   * 409 means "the thing you are acting on moved on", and reef has more than
   * one of those. A stale editor is one; an action that was already decided,
   * or applied and then failed at the server, is another — and reporting those
   * as "someone else saved this workflow while you were editing" told an
   * approver a confidently wrong story about what had happened. So this
   * narrows on the error code the control plane sends rather than on the
   * status alone, and anything else falls through to its own message.
   */
  if (response.status === 409 && body.error === 'stale_editor') {
    throw new StaleEditorError(
      String(body.path ?? ''),
      typeof body.revision === 'number' ? body.revision : null,
    );
  }
  if (response.status === 409 && body.error === 'already_exists') {
    throw new AlreadyExistsError(String(body.message ?? 'That id is already taken.'));
  }
  if (response.status === 429) {
    throw new DeploymentAtCapacityError(
      String(body.message ?? 'This deployment is at its run ceiling.'),
      String(body.ceiling ?? 'unknown'),
    );
  }

  /*
   * `reason` as well as `message`, because the two are used for different
   * shapes and both are the server explaining itself. An endpoint that refuses
   * a decision — "not applied: the connection is not authorized for you" — puts
   * it in `reason`, and dropping it left the UI showing "409 Conflict" for a
   * failure that had a perfectly good sentence attached.
   */
  const explanation = [body.message, body.reason].find((value) => typeof value === 'string');
  throw new Error(explanation ?? `${response.status} ${response.statusText}`);
}
